import { useState } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { saveNeedsFromNotes } from '../../utils/needsService';
import { useAuth } from '../../contexts/AuthContext';
import { WorshipReport } from '../../types/worshipReport';
import GeneralInfo from '../NewReport/GeneralInfo';
import AttendanceSection from '../NewReport/AttendanceSection';
import { calculateTotalParticipants, calculateNewMembersTotal } from '../../utils/calculations';
import { X } from 'lucide-react';
import { toast } from 'sonner';

interface EditWorshipReportFormProps {
  report: WorshipReport;
  onClose: () => void;
  onUpdate?: () => void;
}

export default function EditWorshipReportForm({ report, onClose, onUpdate }: EditWorshipReportFormProps) {
  const { user } = useAuth();
  const [formData, setFormData] = useState<WorshipReport>({
    ...report,
    attendance: { ...report.attendance },
    newMembers: { ...report.newMembers }
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      if (!report.id) {
        throw new Error('ID du rapport manquant');
      }

      const totalParticipants = calculateTotalParticipants(formData);
      const totalNewMembers = calculateNewMembersTotal(formData.newMembers);
      
      const reportRef = doc(db, 'worshipReports', report.id);
      await updateDoc(reportRef, {
        ...formData,
        totalParticipants,
        totalNewMembers,
        updatedBy: user?.id,
        updatedAt: new Date()
      });

      // Sauvegarder les besoins si présents et modifiés
      if (formData.needsNotes && formData.needsNotes !== report.needsNotes) {
        await saveNeedsFromNotes({
          reportId: report.id,
          reportType: 'worship',
          department: 'Gestion de Culte',
          needsNotes: formData.needsNotes,
          reportDate: formData.date
        });
      }

      onUpdate?.();
      onClose();
    } catch (error) {
      console.error('Error updating worship report:', error);
      toast.error('Erreur lors de la mise à jour du rapport de culte');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (_section: keyof WorshipReport, data: any) => {
    setFormData(prev => ({
      ...prev,
      ...data
    }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex justify-between items-center">
          <h2 className="text-xl font-semibold">Modifier le rapport de culte</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <GeneralInfo onChange={handleChange as any} initialData={formData} />
          <AttendanceSection 
            onChange={(data) => handleChange('attendance', data)} 
            initialData={formData}
          />
          
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <label className="block text-sm font-medium text-gray-600 mb-2">
              Notes / Observations
            </label>
            <textarea
              placeholder="Observations particulières..."
              className="w-full h-32 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-[#00665C] focus:border-[#00665C]"
              value={formData.notes || ''}
              onChange={(e) => handleChange('notes', { notes: e.target.value })}
            />
          </div>

          {/* Besoins */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <label className="block text-sm font-medium text-gray-600 mb-2">
              Liste des besoins
            </label>
            <textarea
              placeholder="Listez les besoins du département (un besoin par ligne)..."
              className="w-full h-32 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-[#00665C] focus:border-[#00665C]"
              value={formData.needsNotes || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, needsNotes: e.target.value }))}
            />
            <p className="mt-1 text-xs text-gray-500">
              Entrez un besoin par ligne. Ces besoins seront ajoutés à la liste de suivi des besoins du département.
            </p>
          </div>

          <div className="flex justify-end space-x-4 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-800"
              disabled={isSubmitting}
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-6 py-2 bg-[#00665C] text-white rounded-lg hover:bg-[#004d45] focus:outline-none focus:ring-2 focus:ring-[#00665C] focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Mise à jour...' : 'Mettre à jour'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}